/**
 * useSurveyNavigation Hook
 * Handles navigation between survey trees
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { first, last, isEmpty } from 'lodash';
import type {
  UseSurveyNavigationReturn,
  SurveyParams,
  SurveyType,
  ReportStateType,
  SurveyStateType,
} from '../types';
import { getPatientBasePath } from '../../../../../app/route-utils';
import {
  buildSubmissionLockStorageKey,
  isIntegratedVisit,
  setSubmissionLockedAt,
} from '../submissionLock';

interface UseSurveyNavigationProps {
  survey: SurveyType | undefined;
  surveyState: SurveyStateType;
  reportState: ReportStateType;
  onSurveyStateUpdate: (updater: (state: SurveyStateType) => SurveyStateType) => void;
  onReportStateUpdate: (updater: (state: ReportStateType) => ReportStateType) => void;
  updateVisit: (state: ReportStateType, options?: { data?: Record<string, unknown> }, treeKey?: string | null) => void;
  isTreeVisible?: (params: { treeKey: string }) => boolean;
}

export function useSurveyNavigation({
  survey,
  surveyState,
  reportState,
  onSurveyStateUpdate,
  onReportStateUpdate,
  updateVisit,
  isTreeVisible = () => true,
}: UseSurveyNavigationProps): UseSurveyNavigationReturn {
  const nav = useNavigate();
  const location = useLocation();
  const { orgId, shortId, treeKey, referralMessageId } = useParams();
  const patientBasePath = getPatientBasePath(location.pathname);

  // Build base URL with query params preserved
  const baseUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const aid = searchParams.get('aid');
    const eid = searchParams.get('eid');

    let url = `${patientBasePath}/${orgId}/${shortId}`;
    if (aid || eid) {
      const queryParams = new URLSearchParams();
      if (aid) queryParams.set('aid', aid);
      if (eid) queryParams.set('eid', eid);
      url += `?${queryParams.toString()}`;
    }
    return url;
  }, [orgId, shortId, location.search, patientBasePath]);

  const baseUrlWithoutParams = `${patientBasePath}/${orgId}/${shortId}`;
  const finishedUrl = `${baseUrlWithoutParams}/finished${location.search}`;

  // Get current tree index
  const curTreeIdx = useMemo(() => {
    const questionTreeKeys = survey?.questionTreeKeys || [];
    return questionTreeKeys.findIndex((k) => k === surveyState.curTreeKey);
  }, [survey?.questionTreeKeys, surveyState.curTreeKey]);

  // Check if current tree is the last one
  const isLastTree = useMemo(() => {
    const questionTreeKeys = survey?.questionTreeKeys || [];
    return !isEmpty(questionTreeKeys) && last(questionTreeKeys) === surveyState.curTreeKey;
  }, [survey?.questionTreeKeys, surveyState.curTreeKey]);

  // Set current tree key
  const setCurTreeKey = useCallback(
    (nTreeKey: string) => {
      onSurveyStateUpdate((state) => ({
        ...state,
        curTreeKey: nTreeKey,
      }));
    },
    [onSurveyStateUpdate]
  );

  // Start survey - navigate to first tree
  const startSurvey = useCallback(() => {
    const questionTreeKeys = survey?.questionTreeKeys || [];
    const firstTreeKey = first(questionTreeKeys);

    if (!firstTreeKey) {
      console.warn('No tree keys available in survey');
      return;
    }

    nav(`${baseUrlWithoutParams}/${firstTreeKey}${location.search}`);

    // Update visit with reporting status
    updateVisit(
      reportState,
      {
        data: {
          visitedAt: new Date().toISOString(),
          status: 'reporting',
        },
      },
      firstTreeKey
    );
  }, [survey?.questionTreeKeys, baseUrlWithoutParams, location.search, nav, reportState, updateVisit]);

  // Save survey (complete)
  const onSave = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const aid = searchParams.get('aid');
    const eid = searchParams.get('eid');
    const isIntegrated = isIntegratedVisit({ aid, eid, referralMessageId });
    if (!isIntegrated) {
      const storageKey = buildSubmissionLockStorageKey({
        shortId,
        referralMessageId,
        aid,
        eid,
      });
      setSubmissionLockedAt(storageKey, new Date().toISOString());
    }
    updateVisit(reportState, { data: { status: 'reported' } });
    nav(finishedUrl);
  }, [reportState, updateVisit, finishedUrl, location.search, nav, shortId, referralMessageId]);

  // Go to next tree
  const goNextTree = useCallback(() => {
    if (curTreeIdx == null || curTreeIdx < 0) {
      console.warn('Invalid current tree index');
      return;
    }

    const questionTreeKeys = survey?.questionTreeKeys || [];
    let nextTreeKeyIdx = curTreeIdx;
    let destinationTreeKey: string | null = null;

    // Find next visible tree
    while (!destinationTreeKey && nextTreeKeyIdx < questionTreeKeys.length - 1) {
      nextTreeKeyIdx++;
      const nextTreeKey = questionTreeKeys[nextTreeKeyIdx];
      if (nextTreeKey && isTreeVisible({ treeKey: nextTreeKey })) {
        destinationTreeKey = nextTreeKey;
      }
    }

    if (!destinationTreeKey) {
      // Hidden or disabled trees may remain at the tail; complete the survey when no visible next tree exists.
      onSave();
      return;
    }

    // Update visit and navigate
    updateVisit(reportState, {}, destinationTreeKey);
    nav(`${baseUrlWithoutParams}/${destinationTreeKey}${location.search}`);
  }, [
    curTreeIdx,
    survey?.questionTreeKeys,
    isTreeVisible,
    reportState,
    updateVisit,
    baseUrlWithoutParams,
    location.search,
    nav,
    onSave,
  ]);

  // Go to previous visible tree
  const goPrevTree = useCallback(() => {
    const questionTreeKeys = survey?.questionTreeKeys || [];
    if (questionTreeKeys.length === 0) {
      nav(-1);
      return;
    }

    const currentIndex = questionTreeKeys.findIndex((k) => k === surveyState.curTreeKey);
    if (currentIndex <= 0) {
      nav(baseUrlWithoutParams + location.search);
      return;
    }

    let prevIdx = currentIndex - 1;
    let destinationTreeKey: string | null = null;

    while (prevIdx >= 0 && !destinationTreeKey) {
      const candidate = questionTreeKeys[prevIdx];
      if (candidate && isTreeVisible({ treeKey: candidate })) {
        destinationTreeKey = candidate;
      }
      prevIdx--;
    }

    if (!destinationTreeKey) {
      nav(baseUrlWithoutParams + location.search);
      return;
    }

    updateVisit(reportState, {}, destinationTreeKey);
    nav(`${baseUrlWithoutParams}/${destinationTreeKey}${location.search}`);
  }, [
    survey?.questionTreeKeys,
    surveyState.curTreeKey,
    nav,
    baseUrlWithoutParams,
    location.search,
    isTreeVisible,
    updateVisit,
    reportState,
  ]);

  // Interim save (auto-save)
  const onInterimSave = useCallback(() => {
    // Intentionally no-op to avoid excessive network saves.
    // Saves should happen on page transitions or completion.
  }, []);

  // Navigate wrapper
  const navigate = useCallback(
    (path: string) => {
      nav(path);
    },
    [nav]
  );

  return {
    baseUrl,
    finishedUrl,
    params: { orgId, shortId, treeKey, referralMessageId } as SurveyParams,
    goNextTree,
    goPrevTree,
    startSurvey,
    onSave,
    onInterimSave,
    setCurTreeKey,
    navigate,
  };
}
